import type { User } from '@/types'

/** IAM app code required for Engine admin modules. */
export const RFID_APP_CODE = 'RFID'

/** IAM role name required within the RFID app. */
export const RFID_ADMIN_ROLE = 'RFID Admin'

function norm(value: string): string {
  return value.trim().toLowerCase().replace(/[\s_-]+/g, ' ')
}

function isRfidAppRef(appRef: Record<string, unknown>): boolean {
  const code = String(appRef.app_code ?? appRef.appCode ?? appRef.code ?? '').trim()
  const name = String(appRef.app_name ?? appRef.appName ?? appRef.name ?? '').trim()
  return norm(code) === norm(RFID_APP_CODE) || norm(name) === norm(RFID_APP_CODE)
}

function roleNamesFromEntry(roleEntry: unknown): string[] {
  if (typeof roleEntry === 'string') return [roleEntry]
  if (!roleEntry || typeof roleEntry !== 'object') return []
  const row = roleEntry as Record<string, unknown>
  const out: string[] = []
  for (const key of ['role_name', 'roleName', 'name', 'code', 'role_code', 'roleCode'] as const) {
    const v = row[key]
    if (typeof v === 'string' && v.trim()) out.push(v)
  }
  return out
}

function rolesForRfidApp(user: User): string[] {
  const raw = user as Record<string, unknown>
  const out: string[] = []

  const applications = raw.applications
  if (Array.isArray(applications)) {
    for (const entry of applications) {
      if (!entry || typeof entry !== 'object') continue
      const row = entry as Record<string, unknown>
      const appRef =
        row.application && typeof row.application === 'object'
          ? (row.application as Record<string, unknown>)
          : row
      if (!isRfidAppRef(appRef)) continue
      const roles = row.roles
      if (!Array.isArray(roles)) continue
      for (const r of roles) out.push(...roleNamesFromEntry(r))
    }
  }

  const appRoles = raw.app_roles
  if (Array.isArray(appRoles)) {
    for (const entry of appRoles) {
      if (!entry || typeof entry !== 'object') continue
      const row = entry as Record<string, unknown>
      if (!isRfidAppRef(row)) continue
      const roles = row.roles
      if (!Array.isArray(roles)) continue
      for (const r of roles) out.push(...roleNamesFromEntry(r))
    }
  }

  return out
}

function hasRfidAppAssignment(user: User): boolean {
  const raw = user as Record<string, unknown>
  const applications = raw.applications
  if (Array.isArray(applications)) {
    for (const entry of applications) {
      if (!entry || typeof entry !== 'object') continue
      const row = entry as Record<string, unknown>
      const appRef =
        row.application && typeof row.application === 'object'
          ? (row.application as Record<string, unknown>)
          : row
      if (isRfidAppRef(appRef)) return true
    }
  }

  const appRoles = raw.app_roles
  if (Array.isArray(appRoles)) {
    for (const entry of appRoles) {
      if (!entry || typeof entry !== 'object') continue
      if (isRfidAppRef(entry as Record<string, unknown>)) return true
    }
  }

  return false
}

function isRfidAdminRoleName(role: string): boolean {
  return norm(role) === norm(RFID_ADMIN_ROLE)
}

/**
 * Engine admin modules require IAM assignment:
 * app `RFID` AND role `RFID Admin` (on that app).
 *
 * Also accepts a top-level `role` / `role_name` of `RFID Admin` when the user
 * is assigned the RFID app (covers IAM payloads that flatten the role).
 */
export function canAccessRfidEngineAdmin(user: User | null | undefined): boolean {
  if (!user) return false
  if (!hasRfidAppAssignment(user)) return false

  const appRoles = rolesForRfidApp(user)
  if (appRoles.some(isRfidAdminRoleName)) return true

  const topLevel = [
    user.role,
    user.pod_role,
    typeof (user as Record<string, unknown>).role_name === 'string'
      ? String((user as Record<string, unknown>).role_name)
      : undefined,
  ].filter((r): r is string => typeof r === 'string' && r.trim().length > 0)

  return topLevel.some(isRfidAdminRoleName)
}
